<script lang="ts">
	import { OvalEffect, DatoImage, StatusLabel } from '@atoms';
	import { DateFormats, Theme, Breakpoints } from '@tools';
	import { Logo } from '@icons';
	import type { ShowingType } from '$lib/types/Showings';
	import EventLabels from '@comp/EventLabels.svelte';

	let {
		data,
		showMore = false,
		onGenreClick
	}: {
		data: ShowingType;
		showMore: boolean;
		onGenreClick?: (genre: { id: string; slug: string }) => void;
	} = $props();
	const genres = $derived(data.genres ?? []);

	let ratio = $state(0);
	let hoverTrigger: HTMLElement | undefined = $state(undefined);
</script>

{#key data.id}
	<a class="container" href={data.path} bind:this={hoverTrigger}>
		{#if Breakpoints.sm}
			<OvalEffect color={Theme.context?.theme?.secondary} bind:ratio {hoverTrigger} />
		{/if}
		<EventLabels
			status={data.status}
			transform={`translateY(${ratio * -100}%)`}
			vertical={!Breakpoints.sm}
		/>
		<div class="image-container">
			{#if data.poster}
				<div
					class="image-zoomer"
					style:transform={Breakpoints.sm ? `scale(${1 + ratio * 0.1})` : undefined}
				>
					<DatoImage responsiveImage={data.poster} />
				</div>
			{:else}
				<Logo />
			{/if}
		</div>
		{#if data.badge}
			<StatusLabel content={data.badge} variant={'darker'} />
		{/if}
		<h5 class="title text:h5" class:double-line={!data.subtitle}>{data.title}</h5>
		{#if data.subtitle}
			<p class="subtitle">{data.subtitle}</p>
		{/if}
		<div class="date">
			{DateFormats.dateWithWeekday(data.date)}
			{#if showMore}
				+ flere
			{/if}
		</div>
		<div class="genres">
			{#each genres as genre, index}
				<span
					id={index.toString()}
					class="genre"
					role="button"
					tabindex="0"
					onkeydown={(e) => {
						if (onGenreClick && (e.key === 'Enter' || e.key === ' ')) {
							onGenreClick(genre);
							e.preventDefault();
						}
					}}
					onclick={(e) => {
						if (onGenreClick) {
							onGenreClick(genre);
							e.preventDefault();
						}
					}}>#{genre.slug.replace('born', 'børn')}</span
				>&nbsp;
			{/each}
		</div>
	</a>
{/key}

<style lang="scss">
	.container {
		position: relative;
		display: grid;
		grid-template-columns: 7.75em max-content 1fr;
		grid-template-rows: max-content max-content 1fr max-content;
		grid-template-areas:
			'poster badge badge'
			'poster title title'
			'poster subtitle subtitle'
			'poster date genres';
		gap: 0 $gap3;
		padding-right: $gap3;
		background-color: $colorCardBackground;
		color: $colorCardForeground;
		text-decoration: none;
		min-width: 0;
		max-width: 100%;

		&:hover .image-zoomer {
			transform: scale(1.1);
		}

		:global(.OvalEffect) {
			z-index: 3;
			width: calc(100% - 7.75em);
			left: unset;
			right: 0;
		}

		:global(> .EventLabels) {
			height: 100%;
		}

		:global(> .EventLabels .StatusLabel) {
			height: 100%;
		}

		:global(> .StatusLabel) {
			padding-left: $gap3;
			padding-right: $gap3;
			margin: 0 calc($gap3 * -1);
			margin-bottom: calc($gap2 * -1);
			grid-area: badge;
		}

		:global(> .StatusLabel span) {
			position: relative;
			z-index: 3;
		}

		@include sm {
			grid-template-columns: max-content 1fr;
			grid-template-rows: max-content max-content max-content minmax(max-content, 1fr) max-content;
			grid-template-areas:
				'poster poster'
				'badge badge'
				'title title'
				'subtitle subtitle'
				'date genres';
			gap: 0 $gap3;
			padding-right: 0;
			padding-bottom: $gap4;
			aspect-ratio: 1 / #{$ratio2};

			&:hover .image-zoomer {
				transform: none;
			}

			:global(> .EventLabels) {
				height: auto;
			}

			:global(> .EventLabels .StatusLabel) {
				height: auto;
			}

			:global(.OvalEffect) {
				width: 100%;
			}

			:global(> .StatusLabel) {
				padding-left: $gap4;
				padding-right: $gap4;
				margin: 0;
				z-index: 2;
				margin-top: calc((12px * 1.5 + 8px) * -1);
			}
		}
	}

	.image-zoomer {
		position: relative;
		width: 100%;
		height: 100%;
		transition: transform $transition;

		@include sm {
			transition: none;
		}
	}

	.image-container {
		position: relative;
		z-index: 1;
		overflow: hidden;
		grid-area: poster;

		:global(svg) {
			height: 20%;
			width: 20%;
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			color: rgba(0, 0, 0, 0.08);
		}

		@include sm {
			aspect-ratio: 1 / 1.092;
		}
	}

	.title {
		padding-top: $gap3;
		grid-area: title;
		display: -webkit-box;
		box-sizing: initial;
		line-height: 1.25em;
		white-space: normal;
		z-index: 4;
		overflow-wrap: break-word;
		word-wrap: break-word;
		hyphens: auto;
		overflow: hidden;
		text-overflow: ellipsis;
		-webkit-box-orient: vertical;
		max-height: 1.25em;
		-webkit-line-clamp: 1;
		line-clamp: 1;
		margin-bottom: $gap2;

		&.double-line {
			max-height: #{1.25 * 2}em;
			-webkit-line-clamp: 2;
			line-clamp: 2;
		}

		@include sm {
			padding: 0 $gap4;
			margin-bottom: 0;
			margin-top: $gap3;
			max-height: #{1.25 * 2}em;
			-webkit-line-clamp: 2;
			line-clamp: 2;
		}

		@include lg {
			max-height: #{1.25 * 2}em;
			-webkit-line-clamp: 2;
			line-clamp: 2;

			&.double-line {
				max-height: #{1.25 * 3}em;
				-webkit-line-clamp: 3;
				line-clamp: 3;
			}
		}
	}

	.subtitle {
		grid-area: subtitle;
		position: relative;
		line-height: 1.25em;
		height: 1.25em;
		overflow: hidden;
		text-overflow: ellipsis;
		z-index: 4;
		width: 100%;
		min-width: 0;
		margin-bottom: $gap2;

		@include sm {
			padding: 0 $gap4;
			margin-bottom: 0;
			margin-top: $gap2;
			/* margin-top: #{$gap2 * -1}; */
		}
	}

	.date {
		padding-bottom: $gap3;
		grid-area: date;
		position: relative;
		z-index: 4;
		flex: 0 0 auto;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 100%;
		flex: 0 1 auto;

		@include sm {
			font-weight: 500;
			padding-left: $gap4;
			padding-bottom: 0;
		}
	}

	.genres {
		padding-bottom: $gap3;
		grid-area: genres;
		justify-self: end;
		align-self: center;
		position: relative;
		text-transform: lowercase;
		z-index: 4;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 100%;
		flex: 0 1 auto;
		min-width: 0;

		@include sm {
			font-weight: 500;
			padding-right: $gap4;
			padding-bottom: 0;
		}
	}

	.genre {
		display: inline;

		&:hover,
		&:focus-visible {
			text-decoration: underline;
		}
	}
</style>
